import { apiUrl } from "../../api/endpoints";
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';

import Alert from '../Alert';
import Spinner from '../Spinner';
import { useAuth } from '../../hooks/AuthProvider';
import { LanguageContext } from '../../context/LanguageContext';
import automationsTranslations from '../../languages/automation.json';
import { getErrorMessage } from '../../utils/errorUtils';

const createAlertState = (isRtl) => ({
  open: false,
  message: '',
  type: 'info',
  position: isRtl ? 'left' : 'right',
});

export default function AutomationTransferOrganizationModal({ state, setState, refresh }) {
  const { permissions } = useAuth();
  const canTransferAutomations = Boolean(permissions?.edit_automations);
  const { lang } = useContext(LanguageContext);
  const isRtl = ['ar', 'ur'].includes(lang);
  const t = automationsTranslations?.[lang]?.transferOrganization || {};

  const [transferData, setTransferData] = useState(null);
  const [destinationOrganization, setDestinationOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alertState, setAlertState] = useState(createAlertState(isRtl));

  const showAlert = useCallback((type, message) => {
    setAlertState({
      open: true,
      message,
      type,
      position: isRtl ? 'left' : 'right',
    });
  }, [isRtl]);

  const closeModal = useCallback(() => {
    setTransferData(null);
    setDestinationOrganization(null);
    setLoading(false);
    setSubmitting(false);
    setAlertState(createAlertState(isRtl));
    setState({ open: false, id: 0 });
  }, [isRtl, setState]);

  useEffect(() => {
    if (!state?.open || !state?.id || !canTransferAutomations) return;

    let mounted = true;

    const loadTransferTargets = async () => {
      setLoading(true);
      setTransferData(null);
      setDestinationOrganization(null);

      try {
        const response = await axios.get(apiUrl(`automation/${state.id}/transfer-targets`));
        if (!mounted) return;

        const data = response.data?.data || null;
        setTransferData(data);
      } catch (error) {
        if (!mounted) return;
        setTransferData(null);
        showAlert('error', getErrorMessage(error));
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTransferTargets();

    return () => {
      mounted = false;
    };
  }, [canTransferAutomations, showAlert, state?.id, state?.open]);

  const organizationOptions = useMemo(
    () =>
      (Array.isArray(transferData?.targets) ? transferData.targets : []).map((organization) => ({
        value: organization.id,
        label: organization.name,
        ...organization,
      })),
    [transferData?.targets]
  );

  const selectedOrganizationOption = useMemo(
    () =>
      organizationOptions.find(
        (option) => Number(option.value) === Number(destinationOrganization?.id)
      ) || null,
    [destinationOrganization?.id, organizationOptions]
  );

  const linkedDevices = useMemo(
    () =>
      (Array.isArray(transferData?.linked_devices) ? transferData.linked_devices : [])
        .map((device) => ({
          id: device?.id ?? device?.did ?? device?.name,
          label: device?.name || device?.did,
        }))
        .filter((device) => device.label),
    [transferData?.linked_devices]
  );

  const transferAutomation = async () => {
    if (submitting) return;

    if (!destinationOrganization?.id) {
      showAlert('error', t.transferOrgError || 'Please select a destination organization.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        apiUrl(`automation/organization-transfer/${state.id}`),
        { organization_id: destinationOrganization.id },
        { headers: { 'Content-Type': 'application/json' } }
      );

      showAlert(
        'success',
        response.data?.message || t.transferSuccess || 'Automation transferred successfully.'
      );

      refresh?.(response.data?.data);
      setTimeout(() => closeModal(), 1000);
    } catch (error) {
      showAlert('error', getErrorMessage(error));
      setSubmitting(false);
    }
  };

  if (!state?.open) return null;

  if (!canTransferAutomations) {
    return (
      <div id='modal' dir={isRtl ? 'rtl' : 'ltr'}>
        <div id='popup' className='dashboard-modal'>
          <div className='dashboard-header'>
            <div className='dashboard-header-icon'>
              <SyncAltOutlinedIcon />
            </div>
            <div className='dashboard-header-text'>
              <h2>{t.permissionDeniedTitle || 'Permission Denied'}</h2>
              <p>{t.permissionDeniedMessage || 'You do not have permission to transfer automations.'}</p>
            </div>
          </div>
          <div className='modal-divider' />
          <div className='modal-content dashboard-content' />
          <div className='modal-footer dashboard-footer'>
            <button className='btn btn-cancel' onClick={closeModal}>
              {t.buttonClose || 'Close'}
            </button>
            <div />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Alert
        open={alertState.open}
        close={() => setAlertState((current) => ({ ...current, open: false }))}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div id='modal' dir={isRtl ? 'rtl' : 'ltr'}>
        <div id='popup' className='dashboard-modal automation-transfer-modal'>
          <div className='dashboard-header'>
            <div className='dashboard-header-icon'>
              <SyncAltOutlinedIcon />
            </div>
            <div className='dashboard-header-text'>
              <h2>{t.title || 'Transfer Automation'}</h2>
              <p>
                {t.description ||
                  'Move this automation to another organization. The list only shows valid destinations.'}
              </p>
            </div>
          </div>

          <div className='modal-divider' />

          <div className='modal-content dashboard-content'>
            {loading ? (
              <Spinner />
            ) : (
              <div className='form-section'>
                <div className='automation-transfer-summary'>
                  <span className='automation-transfer-label'>
                    {t.currentOrganizationLabel || 'Current organization'}
                  </span>
                  <strong>{transferData?.current_organization?.name || '-'}</strong>
                </div>

                <div className='automation-transfer-note'>
                  {transferData?.restriction_applied
                    ? t.restrictedNote ||
                      'This automation uses linked devices, so it can only be transferred to the linked device organization or one of its parent organizations.'
                    : t.unrestrictedNote ||
                      'This automation is not linked to a device, so it can be transferred to any organization.'}
                </div>

                {linkedDevices.length > 0 ? (
                  <div className='automation-transfer-linked-devices'>
                    <span>{t.linkedDevicesLabel || 'Linked devices'}</span>
                    <div>
                      {linkedDevices.map((device) => (
                        <span key={device.id} className='automation-transfer-device-chip'>
                          {device.label}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className='form-item'>
                  <label className='form-lbl'>{t.selectOrgLabel || 'Destination organization'}</label>
                  <Select
                    inputId='automation-transfer-organization'
                    options={organizationOptions}
                    value={selectedOrganizationOption}
                    onChange={(selectedOption) => setDestinationOrganization(selectedOption || null)}
                    placeholder={t.selectOrgPlaceholder || 'Select destination organization'}
                    classNamePrefix='react-select'
                    isClearable
                    isRtl={isRtl}
                    isDisabled={organizationOptions.length === 0 || submitting}
                  />
                </div>

                {organizationOptions.length === 0 ? (
                  <p className='form-name-helper'>
                    {t.noTransferTargets || 'No valid destination organizations are available for this automation.'}
                  </p>
                ) : null}
              </div>
            )}
          </div>

          <div className='modal-footer dashboard-footer'>
            <button className='btn btn-cancel' onClick={closeModal} disabled={submitting}>
              {t.buttonCancel || 'Cancel'}
            </button>
            <button
              className='btn btn-apply dashboard-primary-btn'
              onClick={transferAutomation}
              disabled={loading || submitting || organizationOptions.length === 0}
            >
              {submitting ? t.buttonTransferring || 'Transferring...' : t.buttonTransfer || 'Transfer'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
